import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { getOpenBets, getBets } from '../lib/api';
import { BetCard } from '../components/bets/BetCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { PageHero } from '../components/PageHero';
import { 
    Search, 
    Filter, 
    TrendingUp,
    RefreshCw,
    Plus,
    Timer,
    Trophy,
    LayoutGrid
} from 'lucide-react';

const categories = [];
const sortOptions = [];

export function BetsPage() {
    const { t } = useTranslation();
    const { address } = useWeb3();
    const [bets, setBets] = useState([]);
    const [shareCounts, setShareCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('open');
    const [category, setCategory] = useState('all');
    const [sortBy, setSortBy] = useState('newest');
    const [searchQuery, setSearchQuery] = useState('');

    const categories = [
        { value: 'all', label: t('bets.categoryAll') },
        { value: 'sports', label: t('bets.catSports') },
        { value: 'crypto', label: t('bets.catCrypto') },
        { value: 'politics', label: t('bets.catPolitics') },
        { value: 'entertainment', label: t('bets.catEntertainment') },
        { value: 'custom', label: t('bets.catCustom') },
    ];

    const sortOptions = [
        { value: 'newest', label: t('bets.sortNewest') },
        { value: 'oldest', label: t('bets.sortOldest') },
        { value: 'highest', label: t('bets.sortHighest') },
        { value: 'lowest', label: t('bets.sortLowest') },
        { value: 'odds_high', label: t('bets.sortOddsHigh') },
        { value: 'odds_low', label: t('bets.sortOddsLow') },
    ];

    const fetchBets = useCallback(async (showRefresh = false) => {
        if (showRefresh) setRefreshing(true);
        else setLoading(true);

        try {
            let data;
            const params = {};
            if (category !== 'all') params.category = category;

            if (activeTab === 'open') {
                data = await getOpenBets(params);
            } else if (activeTab === 'active') {
                params.status = 'active';
                data = await getBets(params);
            } else {
                params.status = 'resolved';
                data = await getBets(params);
            }

            data.sort((a, b) => {
                switch (sortBy) {
                    case 'oldest':
                        return new Date(a.created_at) - new Date(b.created_at);
                    case 'highest':
                        return b.creator_stake - a.creator_stake;
                    case 'lowest':
                        return a.creator_stake - b.creator_stake;
                    case 'odds_high':
                        return b.odds_bps - a.odds_bps;
                    case 'odds_low':
                        return a.odds_bps - b.odds_bps;
                    default:
                        return new Date(b.created_at) - new Date(a.created_at);
                }
            });

            if (searchQuery) {
                data = data.filter(bet => 
                    bet.description.toLowerCase().includes(searchQuery.toLowerCase())
                );
            }

            setBets(data);
            // Bulk fetch share counts
            const ids = data.map(b => b.id).filter(Boolean);
            if (ids.length > 0) {
                try {
                    const API = process.env.REACT_APP_BACKEND_URL;
                    const res = await fetch(`${API}/api/social-sharing/bets/counts`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ bet_ids: ids }),
                    }).then(r => r.json());
                    setShareCounts(res.counts || {});
                } catch (e) { /* non-blocking */ }
            }
        } catch (error) {
            console.error('Failed to fetch bets:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [activeTab, category, sortBy, searchQuery]);

    useEffect(() => {
        fetchBets();
    }, [fetchBets]);

    return (
        <div className="page-enter" data-testid="bets-page">
            <PageHero
                icon={LayoutGrid}
                title={t('bets.browseTitle')}
                highlight={t('bets.browseHighlight')}
                subtitle={t('bets.browseSubtitle')}
                right={
                    <Link to="/create">
                        <Button
                            className="bg-[#FFD800] text-[#003B71] hover:bg-[#FFC800] font-bold btn-press shadow-lg"
                            data-testid="create-bet-button"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            {t('bets.createBetButton')}
                        </Button>
                    </Link>
                }
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
                <TabsList className="bg-white/5 border border-white/10">
                    <TabsTrigger 
                        value="open" 
                        className="data-[state=active]:bg-[#00FF66]/10 data-[state=active]:text-[#00FF66]"
                        data-testid="tab-open"
                    >
                        <Timer className="w-4 h-4 mr-2" />
                        {t('bets.tabOpen')}
                    </TabsTrigger>
                    <TabsTrigger 
                        value="active"
                        className="data-[state=active]:bg-[#00FF66]/10 data-[state=active]:text-[#00FF66]"
                        data-testid="tab-active"
                    >
                        <TrendingUp className="w-4 h-4 mr-2" />
                        {t('bets.tabActive')}
                    </TabsTrigger>
                    <TabsTrigger 
                        value="resolved"
                        className="data-[state=active]:bg-[#00FF66]/10 data-[state=active]:text-[#00FF66]"
                        data-testid="tab-resolved"
                    >
                        <Trophy className="w-4 h-4 mr-2" />
                        {t('bets.tabResolved')}
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Filters */}
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 mb-8">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                        <Input
                            placeholder={t('bets.searchPlaceholder')}
                            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#00FF66]/50"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            data-testid="search-input"
                        />
                    </div>

                    {/* Category */}
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="w-full md:w-48 bg-white/5 border-white/10 text-white" data-testid="category-filter">
                            <Filter className="w-4 h-4 mr-2 text-white/50" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a0a30] border-white/10">
                            {categories.map((cat) => (
                                <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-white/10">
                                    {cat.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* Sort */}
                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-full md:w-48 bg-white/5 border-white/10 text-white" data-testid="sort-filter">
                            <TrendingUp className="w-4 h-4 mr-2 text-white/50" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a0a30] border-white/10">
                            {sortOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value} className="text-white hover:bg-white/10">
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* Refresh */}
                    <Button
                        variant="outline"
                        className="border-white/10 text-white hover:bg-white/5"
                        onClick={() => fetchBets(true)}
                        disabled={refreshing}
                        data-testid="refresh-button"
                    >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>

            {/* Bets Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Array.from({ length: 6 }, (_, i) => `bet-skel-${i}`).map((sk) => (
                        <div key={sk} className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-4">
                            <Skeleton className="h-6 w-20 bg-white/10" />
                            <Skeleton className="h-16 w-full bg-white/10" />
                            <div className="grid grid-cols-3 gap-2">
                                <Skeleton className="h-14 bg-white/10" />
                                <Skeleton className="h-14 bg-white/10" />
                                <Skeleton className="h-14 bg-white/10" />
                            </div>
                            <Skeleton className="h-10 w-full bg-white/10" />
                        </div>
                    ))}
                </div>
            ) : bets.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {bets.map((bet) => (
                        <BetCard key={bet.id} bet={bet} userAddress={address} shareCount={shareCounts[bet.id]} />
                    ))}
                </div>
            ) : (
                <div className="p-16 rounded-2xl bg-white/[0.02] border border-white/5 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#00FF66]/10 flex items-center justify-center mx-auto mb-4">
                        <Timer className="w-8 h-8 text-[#00FF66]" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                        {t('bets.noOpenBetsTitle', { tab: t(`bets.tab${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`).toLowerCase() })}
                    </h3>
                    <p className="text-white/50 mb-6 max-w-md mx-auto">
                        {activeTab === 'open' ? t('bets.noOpenBetsDesc') : t('bets.noBetsFiltersDesc')}
                    </p>
                    {activeTab === 'open' && (
                        <Link to="/create">
                            <Button className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]">
                                <Plus className="w-4 h-4 mr-2" />
                                {t('bets.createABet')}
                            </Button>
                        </Link>
                    )}
                </div>
            )}

            {/* Stats Footer */}
            <div className="mt-12 text-center text-sm text-white/40">
                {t(bets.length === 1 ? 'bets.showing' : 'bets.showingPlural', {
                    count: bets.length,
                    tab: t(`bets.tab${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`).toLowerCase(),
                })}
            </div>
            </div>
        </div>
    );
}
